import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import Page from '../../components/Page';
import Breadcrumb from '../../components/Breadcrumb';
import client from '../../lib/feathers';

/**
 * Everything one browser has ever done.
 *
 * The visits log answers "what happened in this visit". This answers the
 * question you have once a visit looks interesting: is this their first time,
 * do they always arrive the same way, did they ever sign in.
 */

function Stat({ label, value, hint }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="text-lg text-gray-900">{value}</dd>
      {hint && <dd className="text-xs text-gray-400">{hint}</dd>}
    </div>
  );
}

function duration(sec) {
  if (!sec) return '0s';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function VisitorProfile() {
  const { visitorId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    client.service('admin/visitor-profile').get(visitorId)
      .then((p) => { if (live) { setProfile(p); setError(null); } })
      .catch((e) => { if (live) setError(e.message || 'Not found'); });
    return () => { live = false; };
  }, [visitorId]);

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Analytics', href: '/admin/analytics' },
            { name: 'Visitor' },
          ]} />
        </nav>
      </div>

      {error && <div className="py-5 text-sm text-red-700">{error}</div>}
      {!error && !profile && <div className="py-5 text-sm text-gray-500">Loading...</div>}

      {profile && (
        <>
          <div className="py-5">
            <h1 className="text-2xl pb-1">Visitor</h1>
            <p className="font-mono text-xs text-gray-400">{profile.visitor.id}</p>
            {profile.visitor.mergedIntoVisitorId && (
              <p className="mt-2 text-sm text-amber-700">
                Merged into{' '}
                <Link className="underline" to={`/admin/analytics/visitors/${profile.visitor.mergedIntoVisitorId}`}>
                  another visitor
                </Link>
                . This row is kept so the events pointing at it stay valid.
              </p>
            )}
          </div>

          <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 bg-white shadow rounded-lg p-6 mb-6">
            <Stat label="Visits" value={profile.summary.visitCount} />
            <Stat label="Page views" value={profile.summary.eventCount} />
            <Stat label="Actions" value={profile.summary.actionCount} />
            <Stat label="Total time" value={duration(profile.summary.totalSeconds)} />
            <Stat
              label="First seen"
              value={profile.summary.firstVisit ? formatDistanceToNow(new Date(profile.summary.firstVisit), { addSuffix: true }) : '-'}
            />
            <Stat
              label="Last seen"
              value={profile.summary.lastVisit ? formatDistanceToNow(new Date(profile.summary.lastVisit), { addSuffix: true }) : '-'}
            />
          </dl>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Arrived via</h3>
              <p className="text-sm text-gray-600">{(profile.summary.channels || []).join(', ') || '-'}</p>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Devices</h3>
              <p className="text-sm text-gray-600">
                {(profile.summary.devices || []).join(', ') || '-'}
                {profile.summary.surfaces?.length ? ` (${profile.summary.surfaces.join(', ')})` : ''}
              </p>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Signed in as</h3>
              {profile.users.length === 0 && <p className="text-sm text-gray-400 italic">Never signed in</p>}
              {profile.users.map((u) => (
                <p key={u.id} className="text-sm text-gray-600">
                  {/* This page is one BROWSER. A person is routinely several
                      of them, so offer the way out to their whole history
                      rather than leaving this as the end of the road. */}
                  <Link className="text-indigo-600 hover:underline" to={`/analytics/people/${u.id}`}>
                    {u.username || u.email}
                  </Link>
                  <span className="text-xs text-gray-400"> - see all their devices</span>
                </p>
              ))}
              {profile.users.length > 1 && (
                // Not a bug. A device can be handed over, and attribution
                // deliberately stops at the previous person's last session.
                <p className="mt-1 text-xs text-amber-700">Shared device: more than one person has signed in here.</p>
              )}
            </div>
          </div>

          <h2 className="text-lg text-gray-900 mb-2">Visits</h2>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">Device</th>
                  <th className="px-4 py-3">Landed on</th>
                  <th className="px-4 py-3">Did</th>
                  <th className="px-4 py-3">For</th>
                  <th className="px-4 py-3">Who</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {profile.visits.map((v) => (
                  <tr
                    key={v.id}
                    className="text-sm hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate('/admin/analytics')}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {format(new Date(v.startedAt), 'd MMM HH:mm')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {v.channel}{v.utmCampaign ? ` (${v.utmCampaign})` : ''}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{v.deviceType} {v.os} <span className="text-xs text-gray-400">{v.surface}</span></td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{v.landingPath || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{v.eventCount} views, {v.actionCount} actions</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">{duration(v.durationSec)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {v.username || <span className="text-gray-400 italic">anonymous</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Page>
  );
}
